import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Col,
  DatePicker,
  Form,
  Row,
} from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { isNullOrUndefined } from 'ontraccr-common/lib/Common';

import BudgetTrackingEstimates from '../BudgetTrackingEstimates';
import ProjectCurrencyInput from '../../../ProjectCurrencyInput';
import OnTraccrSwitch from '../../../../common/inputs/OnTraccrSwitch';
import DisplayText from '../../../../common/text/DisplayText';
import { ESTIMATED_LABEL_STYLE, ESTIMATED_DISPLAY_TEXT_STYLE } from '../../budgetTrackingHelpers';
import { currencyFormatter } from '../../../../helpers/inputParsers';
import Permissions from '../../../../auth/Permissions';
import { updateProjectFromCostTracking } from '../../../state/projects.actions';
import { getIdMap } from '../../../../helpers/helpers';

const num = (v) => (isNullOrUndefined(v) ? undefined : Number(v));

const toMomentRange = (startDate, endDate) => {
  if (!startDate && !endDate) return undefined;
  const start = startDate ? moment(startDate) : null;
  const end = endDate ? moment(endDate) : null;
  if (!start || !end) return undefined;
  return [start, end];
};

/**
 * Estimates section for Cost Tracking. Edit opens form (values set on Edit click);
 * Save persists via cost tracking route and updates enableBuildEstimate when toggled.
 */
export default function CostTrackingEstimates({
  projectId,
  contractValue,
  contractTotalValue,
  estimatedLaborCost = 0,
  estimatedLabourHours,
  estimatedMaterialCost = 0,
  estimatedOverheadCost = 0,
  estimatedEquipmentCost = 0,
  estimatedBurdenRate = 0,
  estimatedSubContractTotalValue = 0,
  projectCustomCategoryEstimates = [],
  filteredSubContracts = [],
  allocatedHours,
  allocatedColor,
  useBuildEstimate: useBuildEstimateProp = false,
  startDate: startDateProp = null,
  endDate: endDateProp = null,
}) {
  const dispatch = useDispatch();
  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [localUseBuildEstimate, setLocalUseBuildEstimate] = useState(useBuildEstimateProp);
  const [localUseDates, setLocalUseDates] = useState(!!(startDateProp || endDateProp));

  const customCategories = useMemo(() => (
    Object.values(categoriesIdMap || {}).filter((category) => !category?.isDefault)
  ), [categoriesIdMap]);

  const subContractTotalValue = useMemo(() => {
    if (!filteredSubContracts?.length) return estimatedSubContractTotalValue;
    return filteredSubContracts.reduce((sum, sc) => sum + (sc.contractTotal ?? 0), 0);
  }, [filteredSubContracts, estimatedSubContractTotalValue]);

  const customCategoryCostTotal = useMemo(() => (
    (projectCustomCategoryEstimates || []).reduce(
      (sum, { value }) => sum + (Number(value) || 0),
      0,
    )
  ), [projectCustomCategoryEstimates]);

  const estimatedBurdenCost = (estimatedLaborCost ?? 0) * (estimatedBurdenRate ?? 0);

  const totalEstimatedAmount = useMemo(() => currencyFormatter(
    (estimatedMaterialCost ?? 0)
    + (estimatedOverheadCost ?? 0)
    + (estimatedLaborCost ?? 0)
    + (estimatedEquipmentCost ?? 0)
    + estimatedBurdenCost
    + (subContractTotalValue ?? 0)
    + (customCategoryCostTotal ?? 0),
  ), [
    estimatedMaterialCost,
    estimatedOverheadCost,
    estimatedLaborCost,
    estimatedEquipmentCost,
    estimatedBurdenCost,
    subContractTotalValue,
    customCategoryCostTotal,
  ]);

  const expectedProfit = useMemo(() => currencyFormatter(
    (contractTotalValue ?? 0)
    - (estimatedMaterialCost ?? 0)
    - (estimatedOverheadCost ?? 0)
    - (estimatedLaborCost ?? 0)
    - (estimatedEquipmentCost ?? 0)
    - estimatedBurdenCost
    - (subContractTotalValue ?? 0)
    - (customCategoryCostTotal ?? 0),
  ), [
    contractTotalValue,
    estimatedMaterialCost,
    estimatedOverheadCost,
    estimatedLaborCost,
    estimatedEquipmentCost,
    estimatedBurdenCost,
    subContractTotalValue,
    customCategoryCostTotal,
  ]);

  const effectiveUseBuildEstimate = isEditing ? localUseBuildEstimate : useBuildEstimateProp;

  const formInitialValues = useMemo(() => {
    const base = {
      contractValue: contractValue ?? undefined,
      estimatedLabourHours: estimatedLabourHours ?? undefined,
      estimatedLabourCost: estimatedLaborCost ?? undefined,
      estimatedMaterialCost: estimatedMaterialCost ?? undefined,
      estimatedOverheadCost: estimatedOverheadCost ?? undefined,
      estimatedEquipmentCost: estimatedEquipmentCost ?? undefined,
      estimatedBurdenRate: estimatedBurdenRate ?? undefined,
      estimatedSubContractTotalValue: subContractTotalValue ?? undefined,
      dateRange: toMomentRange(startDateProp, endDateProp),
    };
    (projectCustomCategoryEstimates || []).forEach(({ categoryId, value }) => {
      if (categoryId != null) base[`categoryEstimate.${categoryId}`] = value ?? undefined;
    });
    return base;
  }, [
    contractValue,
    estimatedLabourHours,
    estimatedLaborCost,
    estimatedMaterialCost,
    estimatedOverheadCost,
    estimatedEquipmentCost,
    estimatedBurdenRate,
    subContractTotalValue,
    projectCustomCategoryEstimates,
    startDateProp,
    endDateProp,
  ]);

  const handleEditClick = useCallback(() => {
    form?.setFieldsValue(formInitialValues);
    setIsEditing(true);
    setLocalUseBuildEstimate(useBuildEstimateProp);
    setLocalUseDates(!!(startDateProp || endDateProp));
  }, [form, formInitialValues, useBuildEstimateProp, startDateProp, endDateProp]);

  const showDateRange = (isEditing && localUseDates)
    || (!isEditing && startDateProp && endDateProp);

  const customCategoryRows = useMemo(() => {
    const estimates = projectCustomCategoryEstimates || [];
    const estimatesMap = getIdMap(estimates, 'categoryId');
    if (customCategories.length === 0 && !showDateRange) return null;

    const items = customCategories.map((category) => ({ type: 'category', categoryId: category.id, value: estimatesMap[category.id] ?? 0 }));
    if (showDateRange) items.push({ type: 'dateRange' });

    const categoryRows = [];
    let currentRow = [];
    items.forEach((item, index) => {
      currentRow.push(item);
      if (currentRow.length === 3 || index === items.length - 1) {
        categoryRows.push(currentRow);
        currentRow = [];
      }
    });

    return (
      <>
        {categoryRows.map((rowItems) => (
          <Row
            gutter={[20, 20]}
            key={rowItems.map((i) => (i.type === 'dateRange' ? 'dateRange' : i.categoryId)).join('-')}
          >
            {rowItems.map((item) => {
              if (item?.type === 'dateRange') {
                return (
                  <Col span={8} key="dateRange">
                    <Form.Item
                      name="dateRange"
                      label="Start and End Date"
                      style={{ marginBottom: 0 }}
                    >
                      {isEditing && hasWrite ? (
                        <DatePicker.RangePicker style={{ width: '100%' }} className="form-field-datetime-container" />
                      ) : (
                        <DisplayText
                          title={startDateProp && endDateProp ? `${startDateProp} - ${endDateProp}` : ''}
                          style={ESTIMATED_DISPLAY_TEXT_STYLE}
                          displayAsDropdown
                        />
                      )}
                    </Form.Item>
                  </Col>
                );
              }
              const { categoryId, value } = item;
              const { value: categoryValue } = value;
              const category = categoriesIdMap[categoryId];
              const name = category?.name ?? `Category ${categoryId}`;
              return (
                <Col span={8} key={categoryId}>
                  <ProjectCurrencyInput
                    name={`categoryEstimate.${categoryId}`}
                    title={`Estimated ${name} Cost`}
                    isNotDisplay={isEditing && !effectiveUseBuildEstimate}
                    value={categoryValue ?? 0}
                    labelStyle={ESTIMATED_LABEL_STYLE}
                    displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
                    displayAsDropdown
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </>
    );
  }, [
    projectCustomCategoryEstimates,
    categoriesIdMap,
    isEditing,
    effectiveUseBuildEstimate,
    showDateRange,
    hasWrite,
    startDateProp,
    endDateProp,
  ]);

  const checkBoxes = isEditing && hasWrite ? (
    <Row gutter={12} style={{ marginBottom: 16 }}>
      <Col>
        <OnTraccrSwitch
          onChange={() => setLocalUseBuildEstimate((v) => !v)}
          value={localUseBuildEstimate}
          title="Build Estimate?"
          style={{ marginBottom: 10 }}
          textStyle={{ ...ESTIMATED_LABEL_STYLE, marginBottom: 10 }}
        />
      </Col>
      <Col>
        <OnTraccrSwitch
          onChange={() => setLocalUseDates((v) => !v)}
          value={localUseDates}
          title="Add Dates/Timelines?"
          textStyle={ESTIMATED_LABEL_STYLE}
        />
      </Col>
    </Row>
  ) : null;

  const handleSave = useCallback(async () => {
    if (!projectId || !form) return;
    const values = form.getFieldsValue();
    const customCategoryEstimates = (projectCustomCategoryEstimates || []).map(
      ({ categoryId }) => ({
        categoryId: String(categoryId),
        value: Number(values[`categoryEstimate.${categoryId}`]) || 0,
      }),
    );
    let startDate = null;
    let endDate = null;
    const range = values.dateRange;
    const validRange = localUseDates
      && range?.length === 2
      && range[0]?.isValid?.()
      && range[1]?.isValid?.();
    if (validRange) {
      startDate = range[0].format('YYYY-MM-DD');
      endDate = range[1].format('YYYY-MM-DD');
    }
    const projectEstimates = {
      contractValue: num(values.contractValue),
      estimatedLabourCost: num(values.estimatedLabourCost),
      estimatedLabourHours: num(values.estimatedLabourHours),
      estimatedMaterialCost: num(values.estimatedMaterialCost),
      estimatedOverheadCost: num(values.estimatedOverheadCost),
      estimatedEquipmentCost: num(values.estimatedEquipmentCost),
      estimatedBurdenRate: num(values.estimatedBurdenRate),
      estimatedSubContractTotalValue: num(values.estimatedSubContractTotalValue),
      enableBuildEstimate: localUseBuildEstimate,
      startDate,
      endDate,
      customCategoryEstimates,
    };
    await dispatch(updateProjectFromCostTracking(projectId, { projectEstimates }));
    setIsEditing(false);
  }, [projectId, form, projectCustomCategoryEstimates, localUseBuildEstimate, localUseDates]);

  return (
    <div id="estimates">
      <BudgetTrackingEstimates
        showEditButton={hasWrite}
        wrapContentInForm
        form={form}
        onSave={handleSave}
        onEditClick={handleEditClick}
        useEstimates
        isNotDisplay={isEditing}
        useBuildEstimate={effectiveUseBuildEstimate}
        checkBoxes={checkBoxes}
        contractTotalValue={contractTotalValue}
        contractValue={contractValue}
        totalEstimatedAmount={totalEstimatedAmount}
        expectedProfit={expectedProfit}
        stateHours={estimatedLabourHours}
        stateCosts={estimatedLaborCost}
        labourCost={estimatedLaborCost}
        materialCostTotal={estimatedMaterialCost}
        stateOverheadCost={estimatedOverheadCost}
        estimatedOverheadCost={estimatedOverheadCost}
        equipmentCostTotal={estimatedEquipmentCost}
        stateBurdenRate={estimatedBurdenRate}
        estimatedBurdenCost={estimatedBurdenCost}
        subContractTotalValue={subContractTotalValue}
        customCategoryRows={customCategoryRows}
        allocatedHours={allocatedHours}
        allocatedColor={allocatedColor}
      />
    </div>
  );
}

CostTrackingEstimates.propTypes = {
  projectId: PropTypes.string,
  contractValue: PropTypes.number,
  contractTotalValue: PropTypes.number,
  estimatedLaborCost: PropTypes.number,
  estimatedLabourHours: PropTypes.number,
  estimatedMaterialCost: PropTypes.number,
  estimatedOverheadCost: PropTypes.number,
  estimatedEquipmentCost: PropTypes.number,
  estimatedBurdenRate: PropTypes.number,
  estimatedSubContractTotalValue: PropTypes.number,
  projectCustomCategoryEstimates: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.number,
    }),
  ),
  filteredSubContracts: PropTypes.arrayOf(
    PropTypes.shape({ contractTotal: PropTypes.number }),
  ),
  allocatedHours: PropTypes.number,
  allocatedColor: PropTypes.string,
  useBuildEstimate: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

CostTrackingEstimates.defaultProps = {
  projectId: null,
  contractValue: 0,
  contractTotalValue: null,
  estimatedLaborCost: 0,
  estimatedLabourHours: null,
  estimatedMaterialCost: 0,
  estimatedOverheadCost: 0,
  estimatedEquipmentCost: 0,
  estimatedBurdenRate: 0,
  estimatedSubContractTotalValue: 0,
  projectCustomCategoryEstimates: [],
  filteredSubContracts: [],
  allocatedHours: null,
  allocatedColor: undefined,
  useBuildEstimate: false,
  startDate: null,
  endDate: null,
};
